import { useState } from 'react';
import { Invoice } from '../types';
import invoiceService from '../services/invoiceService';
import Swal from 'sweetalert2';

interface UseInvoiceActionsProps {
  showAlert: (type: 'success' | 'error', message: string) => void;
  loadInvoices: () => Promise<void>;
}

interface InvoiceActionHandlers {
  // Estados para modais
  viewModalOpen: boolean;
  viewInvoice: Invoice | null;
  formOpen: boolean;
  selectedInvoice: Invoice | null;

  // Estados para ações em massa
  bulkCancelMode: boolean;
  bulkPrintMode: boolean;
  bulkEditMode: boolean;
  selectedInvoices: number[];
  selectedInvoicesData: Invoice[];

  // Handlers para ações
  handleViewInvoice: (invoiceId: number) => void;
  handleCloseViewModal: () => void;
  //handleEditInvoice: (invoice: Invoice) => void;
  //handleEditFromView: (invoice: Invoice) => void;
  //handleCancelInvoice: (invoice: Invoice) => Promise<void>;
  handleCheckGatewayStatus: (invoice: Invoice) => Promise<void>;
  handleProcessBoleto: (invoice: Invoice) => Promise<void>;

  // Handlers para ações em massa
  //handleBulkCancelMode: () => void;
  //handleBulkPrintMode: () => void;
  //handleBulkEditMode: () => void;
  //handleCancelBulkMode: () => void;
  handleCancelBulkMode: () => void;
  handleSelectInvoice: (invoiceId: number, selected: boolean) => void;
  handleMassCancel: (invoicesToCancel?: number[]) => Promise<void>;
  handleMassPrint: () => Promise<string | undefined>;
  handleMassEdit: () => Promise<void>;
  handleMassDelete: (invoicesToDelete?: number[]) => Promise<void>;

  // Handlers para gerenciar modais
  setFormOpen: (open: boolean) => void;
  setSelectedInvoice: (invoice: Invoice | null) => void;

  // Função para verificar se há faturas selecionadas
  hasSelectedInvoices: boolean;
}

export const useInvoiceActions = ({
  showAlert,
  loadInvoices
}: UseInvoiceActionsProps): InvoiceActionHandlers => {
  // Estados para modal de visualização
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewInvoice, setViewInvoice] = useState<Invoice | null>(null);

  // Estados para formulário de edição
  const [formOpen, setFormOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  // Estados para cancelamento em massa
  const [bulkCancelMode, setBulkCancelMode] = useState(false);
  const [bulkPrintMode, setBulkPrintMode] = useState(false);
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState<number[]>([]);
  const [selectedInvoicesData, setSelectedInvoicesData] = useState<Invoice[]>([]);

  // Estados para formulário de edição
  const [editFormOpen, setEditFormOpen] = useState(false);

  // Função para visualizar fatura
  const handleViewInvoice = (invoiceId: number) => {
    setViewInvoice({ id: invoiceId } as Invoice);
    setViewModalOpen(true);
  };

  // Função para fechar modal de visualização
  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setViewInvoice(null);
  };

  // Função para editar fatura
  /*const handleEditInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setEditFormOpen(true);
    setViewModalOpen(false);
  };*/

  // Função para editar fatura a partir do modal de visualização
  /*const handleEditFromView = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setEditFormOpen(true);
    setViewModalOpen(false);
  };*/

  // Função para cancelar fatura
  /*const handleCancelInvoice = async (invoice: Invoice) => {
    console.log('🔄 [INVOICE ACTIONS] Iniciando cancelamento da fatura:', invoice.id);
    
    // Chamar cancelamento em massa com a fatura específica
    await handleMassCancel([invoice.id]);
    return;
    try {
      console.log('🔄 [INVOICE ACTIONS] Iniciando cancelamento da fatura:', invoice.id);
      
      const result = await Swal.fire({
        title: 'Cancelar Fatura',
        text: `Deseja realmente cancelar a fatura #${invoice.id}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Sim, cancelar!',
        cancelButtonText: 'Não, manter'
      });

      if (!result.isConfirmed) {
        console.log('🔄 [INVOICE ACTIONS] Cancelamento abortado pelo usuário');
        return;
      }

      console.log('🔄 [INVOICE ACTIONS] Mostrando alerta de processamento...');
      Swal.fire({
        title: 'Processando...',
        text: 'Cancelando fatura, aguarde...',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      console.log('🔄 [INVOICE ACTIONS] Chamando API de cancelamento...');
      const response = await invoiceService.cancelInvoice(invoice.id);
      console.log('🔄 [INVOICE ACTIONS] Resposta da API:', response);

      // Fechar alerta de processamento
      await Swal.close();

      if (response && response.success) {
        console.log('🔄 [INVOICE ACTIONS] Fatura cancelada com sucesso');
        await Swal.fire({
          title: 'Sucesso!',
          text: 'Fatura cancelada com sucesso!',
          icon: 'success',
          confirmButtonText: 'OK'
        });
        await loadInvoices();
      } else {
        throw new Error(response?.message || 'Erro ao cancelar fatura');
      }
    } catch (error: any) {
      console.error('❌ [INVOICE ACTIONS] Erro ao cancelar fatura:', error);
      
      // Verificar se é erro 404 do gateway (cobrança não encontrada)
      if (error.message?.includes('404') || error.message?.includes('não encontrada')) {
        await Swal.fire({
          title: 'Sucesso!',
          text: 'Fatura cancelada com sucesso! (Cobrança não encontrada no gateway)',
          icon: 'success',
          confirmButtonText: 'OK'
        });
        await loadInvoices(); // Recarregar mesmo assim, pois pode ter sido cancelada no sistema
      } else {
        await Swal.fire({
          title: 'Erro!',
          text: error.message || 'Erro ao cancelar fatura',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    }
  };*/

  // Função para cancelar faturas em massa
  const handleMassCancel = async (invoicesToCancel?: number[]) => {
    // Se invoicesToCancel não for um array válido, usar selectedInvoices
    const faturasParaCancelar = (Array.isArray(invoicesToCancel) && invoicesToCancel.length > 0)
      ? invoicesToCancel
      : selectedInvoices;

    // Validação para garantir que faturasParaCancelar é um array válido
    if (!faturasParaCancelar || !Array.isArray(faturasParaCancelar) || faturasParaCancelar.length === 0) {
      showAlert('error', 'Nenhuma fatura selecionada para cancelamento.');
      return;
    }

    let msg = '';
    if (faturasParaCancelar.length === 1) {
      msg = `Deseja cancelar a fatura #${faturasParaCancelar[0]}?`;
    } else {
      msg = `Deseja cancelar ${faturasParaCancelar.length} faturas selecionadas?`;
    }

    const { value: motivo } = await Swal.fire({
      title: 'Cancelar Faturas',
      text: msg,
      input: 'textarea',
      inputLabel: 'Motivo do cancelamento *',
      inputPlaceholder: 'Digite o motivo do cancelamento (obrigatório)...',
      inputValidator: (value) => {
        if (!value || value.trim().length === 0) {
          return 'O motivo é obrigatório';
        }
        if (value.trim().length < 3) {
          return 'O motivo deve ter pelo menos 3 caracteres';
        }
        return null;
      },
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sim, cancelar!',
      cancelButtonText: 'Não, manter',
      allowOutsideClick: false,
      allowEscapeKey: false,
      customClass: {
        container: 'swal-zindex'
      }
    });

    if (!motivo) {
      console.log('🔄 [INVOICE ACTIONS] Cancelamento de faturas abortado pelo usuário');
      return;
    }

    // Buscar as faturas para exibir informações
    const faturasParaCancelarData = faturasParaCancelar.map(id => ({
      id,
      status: 'pendente'
    }));

    // Criar HTML para o progresso
    const createProgressHTML = (faturas: any[]) => {
      return `
        <div style="text-align: left; max-height: 300px; overflow-y: auto;">
          <h4 style="margin-bottom: 15px;">Progresso do Cancelamento</h4>
          ${faturas.map(fatura => `
            <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid #eee;">
              <span style="font-weight: bold;">Fatura #${fatura.id}</span>
              <span style="padding: 4px 8px; border-radius: 4px; font-size: 12px; font-weight: bold; 
                ${fatura.status === 'pendente' ? 'background-color: #fff3cd; color: #856404;' :
          fatura.status === 'sucesso' ? 'background-color: #d4edda; color: #155724;' :
            'background-color: #f8d7da; color: #721c24;'}">
                ${fatura.status === 'pendente' ? '⏳ Pendente' :
          fatura.status === 'sucesso' ? '✅ Cancelado' : '❌ Erro'}
              </span>
            </div>
          `).join('')}
        </div>
      `;
    };

    // Mostrar progresso inicial
    Swal.fire({
      title: 'Cancelando Faturas...',
      html: createProgressHTML(faturasParaCancelarData),
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    let faturasCanceladas = 0;
    let faturasComErro = 0;

    try {
      // Cancelar uma por uma
      for (let i = 0; i < faturasParaCancelarData.length; i++) {
        const fatura = faturasParaCancelarData[i];

        try {
          console.log(`🔄 [INVOICE ACTIONS] Cancelando fatura #${fatura.id}...`);

          // Chamar o método existente de cancelamento individual
          const response = await invoiceService.cancelInvoice(fatura.id, motivo);

          if (response && response.success) {
            fatura.status = 'sucesso';
            faturasCanceladas++;
            console.log(`✅ [INVOICE ACTIONS] Fatura #${fatura.id} cancelada com sucesso`);
          } else {
            fatura.status = 'erro';
            faturasComErro++;
            console.log(`❌ [INVOICE ACTIONS] Erro ao cancelar fatura #${fatura.id}: ${response?.message}`);
          }
        } catch (error: any) {
          fatura.status = 'erro';
          faturasComErro++;
          console.error(`❌ [INVOICE ACTIONS] Erro ao cancelar fatura #${fatura.id}:`, error);
        }

        // Atualizar o progresso visual
        Swal.update({
          html: createProgressHTML(faturasParaCancelarData)
        });

        // Pequena pausa para visualizar o progresso
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Resultado final
      await Swal.fire({
        title: 'Cancelamento Concluído',
        html: `
          <div style="text-align: left;">
            <h4 style="margin-bottom: 15px;">Resumo do Cancelamento</h4>
            <div style="margin-bottom: 10px;">
              <span style="color: #155724; font-weight: bold;">✅ Canceladas: ${faturasCanceladas}</span>
            </div>
            ${faturasComErro > 0 ? `
              <div style="margin-bottom: 10px;">
                <span style="color: #721c24; font-weight: bold;">❌ Com erro: ${faturasComErro}</span>
              </div>
            ` : ''}
            <div style="margin-top: 15px; font-size: 14px; color: #666;">
              Total processado: ${faturasParaCancelarData.length} faturas
            </div>
          </div>
        `,
        icon: faturasComErro === 0 ? 'success' : faturasCanceladas > 0 ? 'warning' : 'error',
        confirmButtonText: 'OK'
      });

      // Recarregar faturas e sair do modo
      await loadInvoices();

      // Só limpar o estado se não for cancelamento individual
      if (!invoicesToCancel) {
        handleCancelBulkMode();
      }

    } catch (error: any) {
      console.error('❌ [INVOICE ACTIONS] Erro geral no cancelamento em massa:', error);

      await Swal.fire({
        title: 'Erro!',
        text: 'Erro inesperado durante o cancelamento em massa',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  };

  // Função para checar status no gateway
  const handleCheckGatewayStatus = async (invoice: Invoice) => {
    if (!invoice.gateway_id || !invoice.gateway_id_externo) {
      showAlert('error', 'Fatura não foi gerada através de um gateway');
      return;
    }

    // Mostrar alert de carregamento
    Swal.fire({
      title: 'Checando fatura no gateway...',
      text: 'Aguarde enquanto consultamos o status no gateway de pagamento',
      icon: 'info',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      console.log('🔍 [INVOICE ACTIONS] Iniciando requisição para fatura ID:', invoice.id);
      const response = await invoiceService.checkGatewayStatus(invoice.id);
      console.log('🔍 [INVOICE ACTIONS] Resposta recebida:', response);

      // Cast para ApiResponse para resolver problemas de tipagem
      const apiResponse = response as any;

      if (apiResponse.success) {
        const data = apiResponse.data;

        // Debug: Verificar estrutura dos dados
        console.log('🔍 [INVOICE ACTIONS] Dados retornados da API:', data);
        console.log('🔍 [INVOICE ACTIONS] dados_gateway:', data.dados_gateway);
        console.log('🔍 [INVOICE ACTIONS] dados_originais:', data.dados_gateway?.dados_originais);
        console.log('🔍 [INVOICE ACTIONS] status_atual:', data.status_atual);
        console.log('🔍 [INVOICE ACTIONS] status_gateway:', data.status_gateway);

        // Tentar diferentes caminhos para obter o status do gateway
        const statusGateway = data.dados_gateway?.dados_originais?.status ||
          data.dados_gateway?.status ||
          data.status_gateway ||
          'UNKNOWN';

        console.log('🔍 [INVOICE ACTIONS] Status do gateway extraído:', statusGateway);
        console.log('🔍 [INVOICE ACTIONS] Comparação - Status atual:', data.status_atual, 'vs Gateway:', statusGateway);

        // Determinar tipo de alert baseado no status
        let alertType: 'success' | 'warning' | 'info' = 'info';
        let alertTitle = 'Status da Fatura';
        let alertText = '';

        // Status que indicam sucesso (pagamentos)
        if (['RECEIVED', 'CONFIRMED', 'RECEIVED_IN_CASH', 'settled'].includes(statusGateway)) {
          alertType = 'success';
          alertTitle = '✅ Fatura Paga!';
          if (data.status_atualizado) {
            alertText = `Pagamento confirmado no gateway!<br>Status atualizado: ${data.status_anterior} → ${data.status_atual}<br>Gateway: ${data.gateway_nome}`;
          } else {
            alertText = `Pagamento já confirmado no gateway!<br>Status: ${data.status_atual}<br>Gateway: ${data.gateway_nome}`;
          }
        }
        // Status que indicam problemas (vencido, cancelado)
        else if (['OVERDUE', 'CANCELLED'].includes(statusGateway)) {
          alertType = 'warning';
          if (statusGateway === 'OVERDUE') {
            alertTitle = '⚠️ Fatura Vencida';
          } else {
            alertTitle = '❌ Fatura Cancelada';
          }
          if (data.status_atualizado) {
            alertText = `Status atualizado no sistema!<br>Status: ${data.status_anterior} → ${data.status_atual}<br>Gateway: ${data.gateway_nome}`;
          } else {
            alertText = `Status confirmado: ${data.status_atual}<br>Gateway: ${data.gateway_nome}`;
          }
        }
        //status que indica deletado
        else if (['deleted'].includes(statusGateway)) {
          alertType = 'warning';
          alertTitle = '❌ Fatura Cancelada';
          alertText = `Fatura deletada e cancelada no gateway<br>Status: ${data.status_atual}<br>Gateway: ${data.gateway_nome}`;
        }
        // Status pendente - incluir também status "aberto" do sistema local
        else if (['PENDING'].includes(statusGateway) || data.status_atual === 'aberto') {
          alertType = 'info';
          alertTitle = '⏳ Fatura Pendente';
          alertText = `Fatura ainda está pendente no gateway<br>Status Local: ${data.status_atual}<br>Status Gateway: ${statusGateway}<br>Gateway: ${data.gateway_nome}`;
        }
        // Status desconhecido
        else {
          alertType = 'info';
          alertTitle = '📋 Status Consultado';
          if (data.status_atualizado) {
            alertText = `Status atualizado: ${data.status_anterior} → ${data.status_atual}<br>Status Gateway: ${statusGateway}<br>Gateway: ${data.gateway_nome}`;
          } else {
            alertText = `Status confirmado: ${data.status_atual}<br>Status Gateway: ${statusGateway}<br>Gateway: ${data.gateway_nome}`;
          }
        }

        console.log('🔍 [INVOICE ACTIONS] Alert final configurado:', {
          alertType,
          alertTitle,
          alertText,
          statusGateway,
          statusAtual: data.status_atual,
          statusAtualizado: data.status_atualizado
        });

        // Mostrar resultado
        await Swal.fire({
          title: alertTitle,
          html: alertText,
          icon: alertType,
          confirmButtonText: 'OK',
          confirmButtonColor: alertType === 'success' ? '#4CAF50' : alertType === 'warning' ? '#FF9800' : '#2196F3'
        });

        // Recarregar as faturas se houve atualização
        if (data.status_atualizado) {
          await loadInvoices();
        }
      } else {
        console.log('🔍 [INVOICE ACTIONS] Resposta sem sucesso:', apiResponse);
        await Swal.fire({
          title: '⚠️ Resposta Inesperada',
          text: apiResponse.message || 'Resposta da API não indica sucesso',
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#FF9800'
        });
      }
    } catch (error: any) {
      console.error('🔍 [INVOICE ACTIONS] Erro ao consultar status:', error);

      // SweetAlert de erro
      await Swal.fire({
        title: '❌ Erro na Consulta',
        text: error.message || 'Erro ao consultar status no gateway',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#f44336'
      });
    }
  };

  // Função para processar boleto
  const handleProcessBoleto = async (invoice: Invoice) => {
    try {
      console.log('🧾 [INVOICE ACTIONS] Processando boleto para fatura:', invoice.id);

      // Mostrar alert de carregamento
      Swal.fire({
        title: 'Processando Boleto...',
        text: 'Aguarde enquanto processamos o boleto',
        icon: 'info',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Simular processamento (substituir pela lógica real quando implementada)
      await new Promise(resolve => setTimeout(resolve, 2000));

      await Swal.fire({
        title: 'Sucesso!',
        text: 'Boleto processado com sucesso!',
        icon: 'success',
        confirmButtonText: 'OK'
      });

      showAlert('success', 'Boleto processado com sucesso!');
      await loadInvoices();
    } catch (error: any) {
      console.error('❌ [INVOICE ACTIONS] Erro ao processar boleto:', error);

      await Swal.fire({
        title: 'Erro!',
        text: error.message || 'Erro ao processar boleto',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  };

  // Função para entrar em modo de cancelamento em massa
  /*const handleBulkCancelMode = () => {
    setBulkCancelMode(true);
    setBulkPrintMode(false);
    setBulkEditMode(false);
    // Não limpar seleção - manter faturas selecionadas
  };*/

  // Função para ativar modo de impressão em massa
  /*const handleBulkPrintMode = () => {
    setBulkPrintMode(true);
    setBulkCancelMode(false);
    setBulkEditMode(false);
    // Não limpar seleção - manter faturas selecionadas
  };*/

  // Função para ativar modo de edição em massa
  /*const handleBulkEditMode = () => {
    setBulkEditMode(true);
    setBulkCancelMode(false);
    setBulkPrintMode(false);
    // Não limpar seleção - manter faturas selecionadas
  };*/

  // Função para sair do modo de ações em massa
  const handleCancelBulkMode = () => {
    setBulkCancelMode(false);
    setBulkPrintMode(false);
    setBulkEditMode(false);
    setSelectedInvoices([]);
    setSelectedInvoicesData([]);
  };

  // Função para selecionar/deselecionar uma fatura para cancelamento em massa
  const handleSelectInvoice = (invoiceId: number, selected: boolean) => {
    setSelectedInvoices(prev => {
      if (selected) {
        return [...prev, invoiceId];
      } else {
        return prev.filter(id => id !== invoiceId);
      }
    });
  };



  // Função para imprimir faturas em massa
  const handleMassPrint = async () => {
    let selectedType = 'cancel';

    const result = await Swal.fire({
      title: 'Escolha o modo de Impressão',
      text: 'Selecione o tipo de impressão desejado:',
      html: `
        <div style="display: flex; justify-content: space-around; align-items: center; margin: 20px 0;">
          <div style="text-align: center; cursor: pointer; padding: 10px; border-radius: 8px; transition: all 0.3s;" 
               onclick="document.getElementById('fatura-bulk-option').click()" 
               onmouseover="this.style.backgroundColor='#f5f5f5'" 
               onmouseout="this.style.backgroundColor='transparent'">
            <img src="/impressaoa4.png" alt="Imprimir Faturas" style="width: 80px; height: 80px; object-fit: contain;">
            <div style="margin-top: 10px; font-weight: bold; color: #1976d2;">Imprimir Faturas</div>
          </div>
          <div style="text-align: center; cursor: pointer; padding: 10px; border-radius: 8px; transition: all 0.3s;" 
               onclick="document.getElementById('carne-bulk-option').click()" 
               onmouseover="this.style.backgroundColor='#f5f5f5'" 
               onmouseout="this.style.backgroundColor='transparent'">
            <img src="/impressaocarne.png" alt="Imprimir Boletos" style="width: 80px; height: 80px; object-fit: contain;">
            <div style="margin-top: 10px; font-weight: bold; color: #1976d2;">Imprimir Boletos</div>
          </div>
          <div style="text-align: center; cursor: pointer; padding: 10px; border-radius: 8px; transition: all 0.3s;" 
               onclick="document.getElementById('capa-carne-bulk-option').click()" 
               onmouseover="this.style.backgroundColor='#f5f5f5'" 
               onmouseout="this.style.backgroundColor='transparent'">
            <img src="/impressaocapacarne.png" alt="Imprimir Capa Carnê" style="width: 80px; height: 80px; object-fit: contain;">
            <div style="margin-top: 10px; font-weight: bold; color: #1976d2;">Imprimir Capa Carnê</div>
          </div>
        </div>
        <button id="fatura-bulk-option" style="display: none;"></button>
        <button id="carne-bulk-option" style="display: none;"></button>
        <button id="capa-carne-bulk-option" style="display: none;"></button>
      `,
      showCancelButton: false,
      showConfirmButton: false,
      allowOutsideClick: true,
      customClass: {
        popup: 'swal2-custom-popup'
      },
      buttonsStyling: false,
      showClass: {
        popup: 'animate__animated animate__fadeInDown'
      },
      hideClass: {
        popup: 'animate__animated animate__fadeOutUp'
      },
      didOpen: () => {
        // Adicionar event listeners para os botões ocultos
        const faturaOption = document.getElementById('fatura-bulk-option');
        const carneOption = document.getElementById('carne-bulk-option');
        const capaCarneOption = document.getElementById('capa-carne-bulk-option');

        if (faturaOption) {
          faturaOption.addEventListener('click', () => {
            selectedType = 'fatura';
            Swal.close();
          });
        }

        if (carneOption) {
          carneOption.addEventListener('click', () => {
            selectedType = 'carne';
            Swal.close();
          });
        }

        if (capaCarneOption) {
          capaCarneOption.addEventListener('click', () => {
            selectedType = 'capa_carne';
            Swal.close();
          });
        }
      }
    });

    return selectedType;
  };



  // Função para deletar faturas em massa
  const handleMassDelete = async (invoicesToDelete?: number[]) => {
    const faturasParaDeletar = (Array.isArray(invoicesToDelete) && invoicesToDelete.length > 0)
      ? invoicesToDelete
      : selectedInvoices;

    if (!faturasParaDeletar || !Array.isArray(faturasParaDeletar) || faturasParaDeletar.length === 0) {
      showAlert('error', 'Nenhuma fatura selecionada para exclusão.');
      return;
    }

    const msg = faturasParaDeletar.length === 1
      ? `Deseja deletar permanentemente a fatura #${faturasParaDeletar[0]}?`
      : `Deseja deletar permanentemente ${faturasParaDeletar.length} faturas selecionadas?`;

    const result = await Swal.fire({
      title: 'Deletar Faturas',
      text: msg,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sim, deletar!',
      cancelButtonText: 'Cancelar',
      allowOutsideClick: false,
      allowEscapeKey: false,
    });

    if (!result.isConfirmed) {
      return;
    }

    Swal.fire({
      title: 'Deletando Faturas...',
      text: 'Aguarde enquanto processamos a exclusão',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    let faturasDeletadas = 0;
    let faturasComErro = 0;
    const erros: string[] = [];

    for (const faturaId of faturasParaDeletar) {
      try {
        const response = await invoiceService.deleteInvoice(faturaId);

        if (response && response.success) {
          faturasDeletadas++;
        } else {
          faturasComErro++;
          erros.push(`Fatura #${faturaId}: ${response?.message || 'Erro desconhecido'}`);
        }
      } catch (error: any) {
        faturasComErro++;
        erros.push(`Fatura #${faturaId}: ${error?.message || 'Erro desconhecido'}`);
      }
    }

    await Swal.fire({
      title: 'Exclusão Concluída',
      html: `
        <div style="text-align: left;">
          <div style="margin-bottom: 10px;">
            <span style="color: #155724; font-weight: bold;">✅ Deletadas: ${faturasDeletadas}</span>
          </div>
          ${faturasComErro > 0 ? `
            <div style="margin-bottom: 10px;">
              <span style="color: #721c24; font-weight: bold;">❌ Com erro: ${faturasComErro}</span>
            </div>
            ${erros.length > 0 ? `<div style="font-size: 13px; color: #666; margin-top: 10px;">${erros.slice(0, 5).join('<br/>')}</div>` : ''}
          ` : ''}
        </div>
      `,
      icon: faturasComErro === 0 ? 'success' : faturasDeletadas > 0 ? 'warning' : 'error',
      confirmButtonText: 'OK'
    });

    await loadInvoices();

    if (!invoicesToDelete) {
      handleCancelBulkMode();
    }
  };

  // Função para editar faturas em massa
  const handleMassEdit = async () => {
    if (selectedInvoices.length === 0) {
      showAlert('error', 'Nenhuma fatura selecionada para edição.');
      return;
    }

    try {
      // Buscar dados das faturas selecionadas
      const invoicesData: Invoice[] = [];

      for (const invoiceId of selectedInvoices) {
        try {
          const response = await invoiceService.getInvoiceById(invoiceId);

          if (response && response.success && response.data) {
            invoicesData.push(response.data);
          }
        } catch (error: any) {
          console.error(`Erro ao carregar fatura ${invoiceId}:`, error);
        }
      }

      // Armazenar dados das faturas selecionadas
      setSelectedInvoicesData(invoicesData);

      // Ativar modo de edição em massa
      setBulkEditMode(true);
      setBulkCancelMode(false);
      setBulkPrintMode(false);

      // Abrir o formulário de edição em massa
      setFormOpen(true);
      setSelectedInvoice(null); // Não há fatura específica selecionada para edição em massa

    } catch (error) {
      console.error('Erro ao carregar dados das faturas:', error);
      showAlert('error', 'Erro ao carregar dados das faturas selecionadas.');
    }
  };

  // Calcular se há faturas selecionadas
  const hasSelectedInvoices = selectedInvoices.length > 0;

  return {
    // Estados
    viewModalOpen,
    viewInvoice,
    formOpen,
    selectedInvoice,
    bulkCancelMode,
    bulkPrintMode,
    bulkEditMode,
    selectedInvoices,
    selectedInvoicesData,

    // Handlers
    handleViewInvoice,
    handleCloseViewModal,
    //handleEditInvoice,
    //handleEditFromView,
    //handleCancelInvoice,
    handleCheckGatewayStatus,
    handleProcessBoleto,
    //handleBulkCancelMode,
    //handleBulkPrintMode,
    //handleBulkEditMode,
    handleCancelBulkMode,
    handleSelectInvoice,
    handleMassCancel,
    handleMassPrint,
    handleMassEdit,
    handleMassDelete,

    // Setters
    setFormOpen,
    setSelectedInvoice,

    // Computed
    hasSelectedInvoices,
  };
}; 